import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  X,
  AlertCircle,
  ChevronDown,
  Check,
  Image as ImageIcon,
  Flashlight,
  FlashlightOff,
} from "lucide-react";
import jsQR from "jsqr";
import { useApp } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SUPPORTED_ASSETS } from "@/lib/constants";
import { getCryptoIconFallback } from "@/lib/cryptoIcons";
import { useQrMaintenance } from "@/lib/qrMaintenance";
import { beginTrustedOverlay, endTrustedOverlay } from "@/lib/appLock";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const CREATE_INTENT_TIMEOUT_MS = 25000;
const CREATE_INTENT_TIMEOUT_MISSING_AMOUNT_MS = 45000;
const QR_STABLE_SCAN_MS = 1000;
const USE_TELEGRAM_NATIVE_SCANNER = true;
// Matches QR_COMMISSION_PCT on the backend. Used only before the intent is quoted.
const QR_DISPLAY_FALLBACK_COMMISSION_PCT = 0.025;
const DEFAULT_QR_PAY_CURRENCY = "USDT";
const QR_PAY_CURRENCIES = SUPPORTED_ASSETS.map((asset) => String(asset.currency || "").toUpperCase()).filter(Boolean);
const QR_PAY_ORDER = new Map(QR_PAY_CURRENCIES.map((code, idx) => [code, idx]));
const QR_PAY_DISPLAY_MAX_DECIMALS = {
  USDT: 6,
  USDC: 6,
  TON: 6,
  SOL: 6,
  TRX: 6,
  BTC: 8,
  ETH: 8,
};
const QR_PAY_META_BY_CODE = SUPPORTED_ASSETS.reduce((acc, asset) => {
  const code = String(asset.currency || "").toUpperCase();
  if (!code) return acc;
  acc[code] = {
    name: String(asset.name || code),
    icon: String(asset.icon || ""),
  };
  return acc;
}, {});
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/i.test(ua);
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

function canUseTelegramNativeScanner() {
  const tg = window?.Telegram?.WebApp;
  return !!(tg && typeof tg.showScanQrPopup === "function" && typeof tg.onEvent === "function");
}

function consumePendingGalleryFileFromHome() {
  try {
    const file = window?.__qrGalleryPendingFile || null;
    delete window.__qrGalleryPendingFile;
    return file;
  } catch (_) {
    return null;
  }
}

function isCameraPermissionError(err) {
  const name = String(err?.name || "");
  return (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError"
  );
}

async function getCameraPermissionState() {
  try {
    if (
      typeof navigator === "undefined" ||
      !navigator.permissions ||
      typeof navigator.permissions.query !== "function"
    ) {
      return "unknown";
    }
    const result = await navigator.permissions.query({ name: "camera" });
    const state = String(result?.state || "");
    if (state === "granted" || state === "denied" || state === "prompt") return state;
  } catch (_) {}
  return "unknown";
}

function tgHapticImpact(style /* "light" | "medium" | "heavy" */) {
  try {
    window?.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.(style);
  } catch (_) {}
}
function tgHapticNotify(type /* "success" | "warning" | "error" */) {
  try {
    window?.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.(type);
  } catch (_) {}
}

function normalizeQrText(txt) {
  try {
    if (/%3A%2F%2F|%3F|%3D/i.test(txt)) {
      const dec = decodeURIComponent(txt);
      if (dec.startsWith("http://") || dec.startsWith("https://")) return dec;
      const m = dec.match(/https?:\/\/[^\s]+/);
      if (m) return m[0];
    }
  } catch (_) {}
  return txt;
}

function parseMultiQrInfo(link) {
  try {
    const u = new URL(String(link || ""));
    const uuid = u.searchParams.get("uuid") || "";
    const host = (u.hostname || "").toLowerCase();
    const amountStr = u.searchParams.get("amount") || "";
    const sumStr = u.searchParams.get("sum") || "";

    let amountRub = null;
    if (amountStr) {
      const parsedAmount = Number(amountStr);
      amountRub = Number.isFinite(parsedAmount) ? parsedAmount : null;
    } else if (sumStr) {
      const parsedSum = Number(String(sumStr).replace(",", "."));
      if (Number.isFinite(parsedSum)) {
        amountRub = host.endsWith("qr.nspk.ru") ? parsedSum / 100 : parsedSum;
      }
    }

    return { uuid, amountRub: Number.isFinite(amountRub) ? amountRub : null };
  } catch (_) {
    return { uuid: "", amountRub: null };
  }
}

function parseRubAmountInput(raw) {
  const cleaned = String(raw || "").trim().replace(/\s+/g, "").replace(",", ".");
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.round(parsed * 100) / 100;
  return rounded > 0 ? rounded : null;
}

function formatRubAmountInput(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return Number.isInteger(n) ? String(n) : String(n.toFixed(2)).replace(/\.?0+$/, "");
}

function isLikelyMissingAmountQr(link) {
  try {
    const u = new URL(String(link || ""));
    const host = (u.hostname || "").toLowerCase();
    const p = u.searchParams;

    if (host.endsWith("qr.nspk.ru")) {
      // If amount is explicitly present, this is not a manual-amount flow.
      if (p.has("sum") || p.has("amount")) {
        return false;
      }
      // Some NSPK links open "enter amount" flow in bank app.
      // In practice this is observed for type=01 without explicit currency and without amount params.
      const qrType = String(p.get("type") || "").trim();
      if (qrType === "01" && !p.has("cur")) {
        return true;
      }
      // NSPK uses `sum` (in kopeks). If absent, we treat QR as open amount.
      return !(p.has("sum") || p.has("amount"));
    }
    if (
      host.endsWith("multiqr.ru") ||
      host.endsWith("platiqr.ru") ||
      host.endsWith("multipay.ru")
    ) {
      // MultiQR/PlatiQR usually include `amount` or `sum`.
      return !(p.has("amount") || p.has("sum"));
    }
  } catch (_) {}
  return false;
}

function getCreateIntentTimeoutMs(link) {
  return isLikelyMissingAmountQr(link) ? CREATE_INTENT_TIMEOUT_MISSING_AMOUNT_MS : CREATE_INTENT_TIMEOUT_MS;
}

function mapVideoToView(vx, vy, vw, vh, viewW, viewH) {
  const scale = Math.max(viewW / vw, viewH / vh); // cover
  const scaledW = vw * scale;
  const scaledH = vh * scale;
  const offsetX = (viewW - scaledW) / 2;
  const offsetY = (viewH - scaledH) / 2;
  return { x: vx * scale + offsetX, y: vy * scale + offsetY };
}

function boxFromPoints(points, padPx) {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs) - padPx;
  const minY = Math.min(...ys) - padPx;
  const maxX = Math.max(...xs) + padPx;
  const maxY = Math.max(...ys) + padPx;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function fmtRub(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  const hasFraction = Math.abs(n - Math.trunc(n)) > 1e-9;
  return (
    n.toLocaleString("ru-RU", {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    }) + " ₽"
  );
}
function fmtNum(x, digits = 2) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("ru-RU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function normalizePayCurrency(raw) {
  const cur = String(raw || "").trim().toUpperCase();
  return QR_PAY_CURRENCIES.includes(cur) ? cur : DEFAULT_QR_PAY_CURRENCY;
}

function getQrPayMaxDecimals(currency) {
  const code = normalizePayCurrency(currency);
  return QR_PAY_DISPLAY_MAX_DECIMALS[code] ?? 6;
}

function getQrPayMinDecimals(currency) {
  const code = normalizePayCurrency(currency);
  return code === "USDT" || code === "USDC" ? 2 : 0;
}

function fmtCurrencyAmount(value, currency, { baseDigits = 4, minDigits } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const maxDigits = getQrPayMaxDecimals(currency);
  const resolvedMin = Number.isInteger(minDigits) ? minDigits : getQrPayMinDecimals(currency);

  let resolvedMax = baseDigits;
  if (Math.abs(n) > 0 && Math.abs(n) < Math.pow(10, -baseDigits)) {
    resolvedMax = maxDigits;
  }
  resolvedMax = Math.max(resolvedMin, Math.min(resolvedMax, maxDigits));

  return n.toLocaleString("ru-RU", {
    minimumFractionDigits: resolvedMin,
    maximumFractionDigits: resolvedMax,
  });
}

function parseSqliteUtc(ts) {
  // Backend stores timestamps as: "YYYY-MM-DD HH:MM:SS" in UTC (sqlite datetime('now')).
  if (!ts || typeof ts !== "string") return null;
  const s = ts.trim();
  // Make it ISO-ish and force UTC.
  const iso = s.includes("T") ? s : s.replace(" ", "T");
  const ms = Date.parse(iso.endsWith("Z") ? iso : `${iso}Z`);
  return Number.isFinite(ms) ? ms : null;
}

function looksLikeTimeoutOrNetwork(message) {
  const msg = String(message || "").toLowerCase();
  return (
    msg.includes("timeout") ||
    msg.includes("econnaborted") ||
    msg.includes("network") ||
    msg.includes("err_network") ||
    msg.includes("failed to fetch")
  );
}

function formatCreateIntentError({ status, detail, message, t }) {
  if (status === 429 && detail?.code === "cooldown") {
    const parsed = Number(detail?.retry_after || 5);
    const seconds = Number.isFinite(parsed) ? Math.max(1, Math.round(parsed)) : 5;
    return t("qr.error_cooldown", { seconds });
  }

  const detailStr = typeof detail === "string" ? detail : "";
  const detailCode = detail && typeof detail === "object" ? String(detail.code || "") : "";

  if (
    status === 409 &&
    (detailStr === "amount_resolving_in_progress" || detailStr === "payment_in_progress")
  ) {
    return t("qr.error_amount_resolving");
  }

  if (status === 400) {
    if (detailCode === "amount_mismatch") {
      return t("qr.error_amount_mismatch", {
        defaultValue: "Сумма в QR и в банковском приложении не совпадает.",
      });
    }
    if (detailCode === "manual_amount_invalid") {
      return t("common.valid_amount_error");
    }
    if (detailCode === "manual_verify_unavailable") {
      return t("qr.error_manual_verify_unavailable", {
        defaultValue: "Не удалось проверить сумму в банковском приложении. Попробуйте еще раз.",
      });
    }
    if (detailCode === "precheck_unavailable") {
      return t("qr.error_scan_transient");
    }
    // Backend uses 400 for QR parse errors and for amount-resolve failures on QR without sum.
    if (detailStr === "resolve_amount_not_found" || detailStr === "resolve_amount_failed") {
      return t("qr.error_processing");
    }
    if (detailStr === "amount_resolving_in_progress") {
      return t("qr.error_amount_resolving");
    }
    return t("qr.error_invalid_or_expired");
  }

  if (looksLikeTimeoutOrNetwork(message)) {
    return t("qr.error_scan_transient");
  }

  return t("qr.error_processing");
}

function isAmountResolvingBusyError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const detailStr = typeof detail === "string" ? String(detail || "").toLowerCase() : "";
  const detailCode =
    detail && typeof detail === "object" ? String(detail.code || "").toLowerCase() : "";
  return (
    status === 409 &&
    (detailStr === "amount_resolving_in_progress" ||
      detailStr === "payment_in_progress" ||
      detailCode === "amount_resolving_in_progress" ||
      detailCode === "payment_in_progress")
  );
}

async function waitFirstFrame(video, timeoutMs = 2500) {
  const t0 = performance.now();
  while (performance.now() - t0 < timeoutMs) {
    if (video?.videoWidth > 0 && video?.videoHeight > 0) return true;
    await sleep(50);
  }
  return video?.videoWidth > 0;
}

const CurrencyIcon = ({ code, size = 28, className = "" }) => {
  const normalized = String(code || "").toUpperCase();
  const meta = QR_PAY_META_BY_CODE[normalized] || {};
  const fallback = getCryptoIconFallback(normalized || "USDT");
  const src = meta.icon || fallback;
  return (
    <img
      src={src}
      alt={normalized}
      width={size}
      height={size}
      className={className}
      loading="lazy"
      onError={(e) => {
        if (e.currentTarget.src !== fallback) {
          e.currentTarget.src = fallback;
        }
      }}
    />
  );
};

export default function QRScannerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user, refreshData, balances, config, market } = useApp();
  const qrMaintenance = useQrMaintenance();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const streamRef = useRef(null);
  const cameraStartingRef = useRef(false);

  const detectorRef = useRef(null);
  const useNativeRef = useRef(false);

  const processingRef = useRef(false);
  const lastRef = useRef({ text: "", ts: 0 });

  const [phase, setPhase] = useState("scanning"); // scanning | locking | error
  const [errorText, setErrorText] = useState("");

  const [videoReady, setVideoReady] = useState(false);
  const [lockScale, setLockScale] = useState(1);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [isTorchBusy, setIsTorchBusy] = useState(false);
  const [nativeScanActive, setNativeScanActive] = useState(false);
  const [nativeInitPending, setNativeInitPending] = useState(USE_TELEGRAM_NATIVE_SCANNER);

  const [boxN, setBoxN] = useState(null);
  const smoothRef = useRef({ has: false, x: 0, y: 0, w: 0, h: 0, lastSeenMs: 0 });

  const [freezeBox, setFreezeBox] = useState(null);
  const freezeRef = useRef({ until: 0 });

  const stableRef = useRef({ lastText: "", firstSeenMs: 0, lastSeenMs: 0 });

  // ===== Bottom sheet =====
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState("preview"); // preview | conflict | manual_amount | paying | success | error
  const [intent, setIntent] = useState(null);
  const [sheetError, setSheetError] = useState("");
  const [isIntentCreating, setIsIntentCreating] = useState(false);
  const [selectedPayCurrency, setSelectedPayCurrency] = useState(DEFAULT_QR_PAY_CURRENCY);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [isCurrencyMenuOpen, setIsCurrencyMenuOpen] = useState(false);
  const currencyMenuRef = useRef(null);
  const manualAmountInputRef = useRef(null);
  const [manualQrLink, setManualQrLink] = useState("");
  const [manualAmountRub, setManualAmountRub] = useState(0);
  const [manualAmountInput, setManualAmountInput] = useState("");
  const [manualAmountError, setManualAmountError] = useState("");

  // Manual cancel UX while waiting for operator (after Pay).
  const [cancelReady, setCancelReady] = useState(false);

  // If the user scans a different QR while an older intent is still active,
  // we should clearly explain the situation instead of silently showing the old intent.
  const [scanAttemptLink, setScanAttemptLink] = useState("");

  const pollRef = useRef({ stop: false, timer: null, tries: 0 });
  const pollMetaRef = useRef({ active: false, lastTickAt: 0, taskId: null, backoffMs: 1000 });

  // When using Telegram native scanner on iOS, we must NOT call getUserMedia.
  const tgScanRef = useRef({
    active: false,
    onQr: null,
    onClose: null,
    closeByApp: false,
    handledQr: false,
  });
  const scannerRecoveryTimerRef = useRef(null);
  const galleryInputRef = useRef(null);
  const galleryErrorTimerRef = useRef(null);
  const autoGalleryPickTriggeredRef = useRef(false);
  const phaseRef = useRef(phase);
  const sheetOpenRef = useRef(sheetOpen);
  const autoPickGallery =
    (() => {
      try {
        return new URLSearchParams(location.search || "").get("pick") === "gallery";
      } catch (_) {
        return false;
      }
    })();

  const getToken = () => {
    try {
      const authHeader =
        api?.defaults?.headers?.common?.Authorization ||
        api?.defaults?.headers?.Authorization ||
        "";
      const m = String(authHeader).match(/^Bearer\s+(.+)$/i);
      if (m?.[1]) return m[1].trim();
    } catch (_) {}

    try {
      return localStorage.getItem("auth_token") || "";
    } catch (_) {
      return "";
    }
  };

  const resolveApiBase = () => {
    const base = api?.defaults?.baseURL || "";
    if (!base) return "https://lightpaytest.duckdns.org/api";
    return base.includes("/api") ? base.replace(/\/+$/, "") : base.replace(/\/+$/, "") + "/api";
  };
  const apiBase = resolveApiBase();

  const endpoints = {
    createIntent: `${apiBase}/qr-intent`,
    quoteIntent: (id) => `${apiBase}/qr-intent/${id}/quote`,
    getActiveIntent: `${apiBase}/qr-intent/active`,
    payIntent: (id) => `${apiBase}/qr-intent/${id}/pay`,
    getIntent: (id) => `${apiBase}/qr-intent/${id}`,
    cancelIntent: (id) => `${apiBase}/qr-intent/${id}/cancel`,
  };

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    sheetOpenRef.current = sheetOpen;
  }, [sheetOpen]);

  useEffect(() => {
    const tg = window?.Telegram?.WebApp;
    if (!tg) return undefined;

    // Telegram красит шапку и низ окна СВОИМ цветом, hex-ом и заранее: взять
    // токен темы он не умеет. Раньше здесь стоял светлый #f2f2f2 — в тёмной
    // теме он рисовал белую рамку вокруг тёмного листа оплаты. Читаем цвет
    // фона со страницы (он и есть фон листа, .lp-solid) и переводим в hex.
    const themeBg = (() => {
      try {
        const parts = getComputedStyle(document.body).backgroundColor.match(/\d+/g);
        if (!parts || parts.length < 3) return null;
        return `#${parts.slice(0, 3).map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`;
      } catch (_) {
        return null;
      }
    })();
    const bgColor = sheetOpen ? themeBg || "#f2f2f2" : "#000000";
    try {
      tg.setHeaderColor?.(bgColor);
      tg.setBackgroundColor?.(bgColor);
      tg.setBottomBarColor?.(bgColor);
    } catch (_) {}
    return undefined;
  }, [sheetOpen]);

  useEffect(() => {
    if (!sheetOpen || sheetMode !== "manual_amount") return;
    const tm = setTimeout(() => manualAmountInputRef.current?.focus?.(), 40);
    return () => clearTimeout(tm);
  }, [sheetOpen, sheetMode]);

  useEffect(() => {
    if (!isCurrencyMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (!currencyMenuRef.current) return;
      if (!currencyMenuRef.current.contains(event.target)) {
        setIsCurrencyMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isCurrencyMenuOpen]);

  // Resume an in-progress intent when the user re-opens the mini-app.
  useEffect(() => {
    const run = async () => {
      try {
        if (!user?.id) return;
        const token = getToken();
        if (!token) return;
        const res = await axios.get(endpoints.getActiveIntent, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 6000,
        });
        const data = res?.data;
        if (!data?.task_id) return;

        // Stop camera and show the bottom sheet
        await stopCamera();
        openSheet(data);

        const st = String(data?.status || "").toLowerCase();
        if (st === "pending") {
          setSheetMode("paying");
          startPolling(data.task_id);
        }
      } catch (_) {
        // no active intent
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const log = (...args) => {
    try {
      // eslint-disable-next-line no-console
      console.debug("[QR]", ...args);
    } catch (_) {}
  };

  const formatApiDetail = (detail) => {
    if (!detail) return "";
    if (typeof detail === "string") return detail;

    // Server refused because QR payments are down for maintenance. Prefer our
    // own translated copy over the Russian default in the 503 body.
    if (detail?.code === "qr_maintenance") return qrMaintenance.short;

    if (detail?.code === "another_active_intent_exists") {
      const id = detail?.existing_task_id;
      return id ? t("qr.error_active_intent", { id }) : t("qr.error_active_intent_generic");
    }

    if (detail?.message) return String(detail.message);

    try {
      return JSON.stringify(detail);
    } catch (_) {
      return t("qr.error_processing");
    }
  };

  const isTerminalStatus = (st) =>
    ["paid", "confirmed", "declined", "failed", "cancelled", "expired", "error"].includes(String(st || "").toLowerCase());

  const isCancellableStatus = (st) => {
    const s = String(st || "").toLowerCase();
    // Auto-cancel only for DRAFT (before the task was sent to staff).
    // Manual cancellation is handled separately via intent.can_cancel.
    return s === "draft";
  };

  const isUserCancelableStatus = (st) => {
    // Prefer server hint when available.
    if (intent?.can_cancel != null) return !!intent.can_cancel;
    const raw = String(intent?.raw_status || "").toUpperCase();
    if (raw) return raw === "DRAFT" || raw === "NEW";
    const s = String(st || "").toLowerCase();
    // Fallback for old backends.
    return s === "draft";
  };

  // Backends sometimes return the intent id as `id` (numeric) or as `task_id`.
  // Support both to avoid silently skipping pay/poll/cancel.
  const getIntentId = (obj) => obj?.task_id ?? obj?.id ?? null;

  const resetManualAmountState = () => {
    setManualQrLink("");
    setManualAmountRub(0);
    setManualAmountInput("");
    setManualAmountError("");
  };

  const setManualAmountValue = (value) => {
    const parsed = Number(value);
    const safe = Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100) / 100) : 0;
    setManualAmountRub(safe);
    setManualAmountInput(formatRubAmountInput(safe));
  };

  const openManualAmountSheet = (qrLink) => {
    resetManualAmountState();
    setIsIntentCreating(false);
    setManualQrLink(String(qrLink || ""));
    setScanAttemptLink("");
    setSheetError("");
    setCancelReady(false);
    setIntent(null);
    setSheetMode("manual_amount");
    setSheetOpen(true);
  };

  const stopPolling = () => {
    pollRef.current.stop = true;
    pollMetaRef.current.active = false;
    if (pollRef.current.timer) {
      clearTimeout(pollRef.current.timer);
      pollRef.current.timer = null;
    }
  };

  const startPolling = (taskIdRaw) => {
    const taskId = getIntentId({ task_id: taskIdRaw, id: taskIdRaw });
    stopPolling();
    pollRef.current.stop = false;
    pollRef.current.tries = 0;

    pollMetaRef.current.active = true;
    pollMetaRef.current.lastTickAt = Date.now();
    pollMetaRef.current.taskId = taskId;
    pollMetaRef.current.backoffMs = 1000;

    const token = getToken();

    const tick = async () => {
      if (pollRef.current.stop) return;
      pollRef.current.tries += 1;
      pollMetaRef.current.lastTickAt = Date.now();

      try {
        const res = await axios.get(endpoints.getIntent(taskId), {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });

        const data = res?.data || {};
        setIntent(data);
        if (data?.pay_currency) {
          setSelectedPayCurrency(normalizePayCurrency(data.pay_currency));
        }

        const st = String(data?.status || "").toLowerCase();
        log("poll", taskId, st, "raw=", data?.id ?? data?.task_id);

        // Terminal statuses
        if (st === "paid" || st === "confirmed") {
          setSheetMode("success");
          tgHapticNotify("success");
          stopPolling();
          try {
            await refreshData?.();
          } catch (_) {}
          // auto close after short delay to avoid "stuck" feeling
          setTimeout(() => {
            try {
              stopCamera();
              navigate("/");
            } catch (_) {}
          }, 1200);
          return;
        }

        if (st === "declined" || st === "failed" || st === "cancelled") {
          setSheetMode("error");
          const raw = String(data?.raw_status || "").toUpperCase();
          setSheetError(
            raw === "CANCELLED" || st === "cancelled"
              ? t("qr.cancelled")
              : t("qr.error_processing")
          );
          tgHapticNotify("error");
          stopPolling();
          return;
        }

        if (st === "expired") {
          setSheetMode("error");
          setSheetError(t("qr.error_processing"));
          tgHapticNotify("error");
          stopPolling();
          return;
        }

        if (st === "error") {
          setSheetMode("error");
          setSheetError(t("qr.error_processing"));
          tgHapticNotify("error");
          stopPolling();
          return;
        }
      } catch (e) {
        const status = e?.response?.status;
        log("poll error", taskId, status, e?.message);
        if (status === 401 || status === 403) {
          setSheetMode("error");
          setSheetError(t("auth.error"));
          tgHapticNotify("error");
          stopPolling();
          return;
        }
        if (status === 404) {
          setSheetMode("error");
          setSheetError(t("qr.error_processing"));
          tgHapticNotify("error");
          stopPolling();
          return;
        }
        if (status === 410) {
          setSheetMode("error");
          setSheetError(t("qr.error_processing"));
          tgHapticNotify("error");
          stopPolling();
          return;
        }
        // other errors: keep polling (temporary network issues)
      }

      // watchdog timeout (do not rely only on timers in Telegram WebView)
      if (pollRef.current.tries > 600) {
        setSheetMode("error");
        setSheetError(t("qr.error_processing"));
        tgHapticNotify("error");
        stopPolling();
        return;
      }

      // adaptive backoff for noisy networks
      const next = pollMetaRef.current.backoffMs || 1000;
      pollMetaRef.current.backoffMs = Math.min(1500, next + 250);
      pollRef.current.timer = setTimeout(tick, next);
    };

    tick();
  };

  const openSheet = (data) => {
    resetManualAmountState();
    setIsIntentCreating(false);
    setIntent(data || null);
    setSelectedPayCurrency(normalizePayCurrency(data?.pay_currency));
    setIsQuoteLoading(false);
    setIsCurrencyMenuOpen(false);
    setSheetError("");
    setCancelReady(false);
    const st = String(data?.status || "").toLowerCase();
    if (st === "pending") {
      setSheetMode("paying");
    } else {
      setSheetMode("preview");
    }
    setSheetOpen(true);
    if (st === "pending") {
      const id = getIntentId(data);
      if (id) setTimeout(() => startPolling(id), 0);
    }
  };

  const openPreviewSheetFromQr = (qrLink, amountRubValue) => {
    const safeAmountRub = Number(amountRubValue);
    if (!Number.isFinite(safeAmountRub) || safeAmountRub <= 0) return;

    resetManualAmountState();
    setScanAttemptLink("");
    setSheetError("");
    setCancelReady(false);
    setIsQuoteLoading(false);
    setIsCurrencyMenuOpen(false);
    setIntent({
      qr_link: String(qrLink || ""),
      amount_rub: Math.round(safeAmountRub * 100) / 100,
      pay_currency: normalizePayCurrency(selectedPayCurrency),
      status: "draft",
      raw_status: "DRAFT",
    });
    setSheetMode("preview");
    setSheetOpen(true);
  };

  // Update cancel readiness while we are waiting.
  useEffect(() => {
    if (!sheetOpen) return;
    if (sheetMode !== "paying") {
      setCancelReady(false);
      return;
    }

    const canCancel = intent?.can_cancel ?? false;
    if (!canCancel) {
      setCancelReady(false);
      return;
    }

    const delaySec = Number(intent?.cancel_after_seconds ?? 30);
    const baseMs = parseSqliteUtc(intent?.updated_at) ?? parseSqliteUtc(intent?.created_at) ?? Date.now();
    const iv = setInterval(() => {
      const ready = Date.now() - baseMs >= delaySec * 1000;
      setCancelReady(ready);
    }, 500);
    return () => clearInterval(iv);
  }, [sheetOpen, sheetMode, intent?.task_id, intent?.updated_at, intent?.created_at, intent?.can_cancel, intent?.cancel_after_seconds]);

  const handleCancel = async () => {
    const id = getIntentId(intent);
    if (!id) return;
    const token = getToken();
    try {
      tgHapticImpact("light");
      await axios.post(
        endpoints.cancelIntent(id),
        { reason: "user_cancel" },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
      );
      // Show a clear state and let user go home (or scan another QR).
      stopPolling();
      setSheetMode("error");
      setSheetError(t("qr.cancelled"));
      setCancelReady(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      log("cancel error", status, detail);
      if (status === 409) {
        // Race: operator has taken it already.
        setCancelReady(false);
        setSheetMode("paying");
        startPolling(id);
        return;
      }
      setSheetMode("error");
      setSheetError(String(detail || t("qr.error_processing")));
    }
  };

  const cancelIfPossible = async (why = "user") => {
    try {
      const id = getIntentId(intent);
      if (!id) {
        log("cancel skip: no intent id", why, intent);
        return;
      }
      const st = String(intent?.status || "").toLowerCase();
      if (!isCancellableStatus(st)) {
        log("cancel skip: not cancellable", id, st, why);
        return;
      }

      const token = getToken();
      log("cancel", id, st, why);
      await axios.post(
        endpoints.cancelIntent(id),
        { reason: why },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }
      );
    } catch (e) {
      // ignore: may already be TAKEN/closed
      log("cancel error", e?.response?.status, e?.message);
    }
  };

  const closeSheet = async () => {
    log("closeSheet", { id: getIntentId(intent), status: intent?.status, mode: sheetMode });
    clearGalleryErrorTimer();
    clearScannerRecoveryTimer();
    stopPolling();
    // If user closes before the task is taken/finished — cancel to release hold.
    await cancelIfPossible("close");
    setSheetOpen(false);
    setSheetMode("preview");
    setIntent(null);
    setSheetError("");
    setIsIntentCreating(false);
    setSelectedPayCurrency(DEFAULT_QR_PAY_CURRENCY);
    setIsQuoteLoading(false);
    setIsCurrencyMenuOpen(false);
    setCancelReady(false);
    setScanAttemptLink("");
    resetManualAmountState();

    processingRef.current = false;
    setPhase("scanning");
    setErrorText("");
    setFreezeBox(null);
    freezeRef.current.until = 0;
    stableRef.current = { lastText: "", firstSeenMs: 0, lastSeenMs: 0 };

    // UX: when user closes the payment sheet, return to the home screen.
    // Restarting camera here is unreliable in Telegram WebView (often results in a black preview).
    try {
      await stopCamera();
    } catch (_) {}
    try {
      navigate("/");
    } catch (_) {}
  };

  const handlePay = async () => {
    const id = getIntentId(intent);
    if (!id) {
      log("pay skip: no intent id", intent);
      return;
    }

    const stNow = String(intent?.status || "").toLowerCase();
    // If the task is already in staff queue (pending), do NOT resend; just resume waiting UI.
    if (stNow === "pending") {
      setSheetMode("paying");
      startPolling(id);
      return;
    }

    // UI already disables button, but keep a minimal guard here.
    if (
      (intent?.available_pay_currency ?? intent?.balance_pay_currency ?? intent?.available_usdt ?? intent?.balance_usdt) != null &&
      (intent?.amount_pay ?? intent?.amount_usdt ?? intent?.amount_usd_est) != null &&
      Number(intent?.available_pay_currency ?? intent?.balance_pay_currency ?? intent?.available_usdt ?? intent?.balance_usdt) + 1e-9 <
        Number(intent?.amount_pay ?? intent?.amount_usdt ?? intent?.amount_usd_est)
    ) {
      setSheetMode("preview");
      setSheetError("");
      return;
    }
    const token = getToken();

    try {
      setSheetMode("paying");
      setSheetError("");
      tgHapticImpact("light");

      log("pay", id);

      await axios.post(
        endpoints.payIntent(id),
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
      );

      // Update wallet balance immediately (HOLD is applied on pay).
      await refreshData?.();

      startPolling(id);
    } catch (e) {
      const rawDetail = e?.response?.data?.detail || e?.message;
      const detailStr = formatApiDetail(rawDetail);
      log("pay error", e?.response?.status, detailStr || rawDetail);
      if (String(rawDetail || "").toLowerCase() === "insufficient_funds") {
        // Refresh intent snapshot to get current selected-currency balance.
        try {
          const res = await axios.get(endpoints.getIntent(id), {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 8000,
          });
          const fresh = res?.data;
          if (fresh) {
            setIntent(fresh);
            if (fresh?.pay_currency) setSelectedPayCurrency(normalizePayCurrency(fresh.pay_currency));
          }
        } catch (_) {
          // Best effort only.
        }
        setSheetMode("preview");
        setSheetError("");
      } else {
        setSheetMode("error");
        setSheetError(t("qr.error_processing"));
      }
      tgHapticNotify("error");
    }
  };

  const handlePayCurrencyChange = async (nextCurrencyRaw) => {
    const nextCurrency = normalizePayCurrency(nextCurrencyRaw);
    const prevCurrency = normalizePayCurrency(selectedPayCurrency);
    setIsCurrencyMenuOpen(false);
    setSelectedPayCurrency(nextCurrency);
    const id = getIntentId(intent);
    if (!id) return;

    const status = String(intent?.status || "").toLowerCase();
    if (status !== "draft") {
      return;
    }
    if (nextCurrency === normalizePayCurrency(intent?.pay_currency || prevCurrency)) {
      return;
    }

    const token = getToken();
    if (!token) return;

    try {
      setIsQuoteLoading(true);
      setSheetError("");
      const res = await axios.post(
        endpoints.quoteIntent(id),
        { pay_currency: nextCurrency },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
      );
      const data = res?.data;
      if (data) {
        setIntent(data);
        if (data?.pay_currency) setSelectedPayCurrency(normalizePayCurrency(data.pay_currency));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (String(detail || "").toLowerCase() === "bad_status") {
        try {
          const fresh = await axios.get(endpoints.getIntent(id), {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 8000,
          });
          if (fresh?.data) {
            setIntent(fresh.data);
            if (fresh.data?.pay_currency) setSelectedPayCurrency(normalizePayCurrency(fresh.data.pay_currency));
          }
        } catch (_) {}
      } else {
        setSheetError(t("qr.error_processing"));
        setSelectedPayCurrency(prevCurrency);
      }
    } finally {
      setIsQuoteLoading(false);
    }
  };

  const handleConflictContinue = () => {
    const id = getIntentId(intent);
    if (!id) {
      closeSheet();
      return;
    }
    const st = String(intent?.status || "").toLowerCase();
    if (st === "pending") {
      setSheetMode("paying");
      startPolling(id);
    } else {
      openSheet(intent);
    }
  };

  const handleConflictCancelAndStartNew = async () => {
    try {
      const id = getIntentId(intent);
      const token = getToken();
      if (!token) throw new Error(t("common.error_no_token"));
      if (!scanAttemptLink) return;

      // Cancel current intent (only if cancellable) and then create a new one for scanned QR.
      const st = String(intent?.status || "").toLowerCase();
      if (id && isUserCancelableStatus(st)) {
        await axios.post(
          endpoints.cancelIntent(id),
          { reason: "conflict_new_qr" },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 7000 }
        );
      }

      const res = await axios.post(
        endpoints.createIntent,
        { user_id: user.id, qr_link: scanAttemptLink, pay_currency: selectedPayCurrency },
        { headers: { Authorization: `Bearer ${token}` }, timeout: getCreateIntentTimeoutMs(scanAttemptLink) }
      );
      const data = res?.data;
      if (!data?.task_id) throw new Error(t("common.error_create_payment"));

      setScanAttemptLink("");
      setIntent(null);
      openSheet(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const message = `${String(e?.code || "")} ${String(e?.message || "")}`.trim();
      const detailStr = formatCreateIntentError({ status, detail, message, t });
      setSheetMode("error");
      setSheetError(detailStr || t("qr.error_processing"));
      tgHapticNotify("error");
    }
  };


  // Telegram/WebView can pause timers; resume polling when app becomes visible again.
  useEffect(() => {
    const onVis = () => {
      try {
        if (document.hidden) return;
        if (!sheetOpen) return;
        if (sheetMode !== "paying") return;
        const id = getIntentId(intent);
        if (!id) return;
        const st = String(intent?.status || "").toLowerCase();
        if (isTerminalStatus(st)) return;
        log("visibility resume polling", id, st);
        startPolling(id);
      } catch (_) {}
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [sheetOpen, sheetMode, intent?.task_id, intent?.status]);

  // Watchdog: if polling "stalls" (no tick for too long) while still on paying screen, restart it.
  useEffect(() => {
    const iv = setInterval(() => {
      try {
        if (!sheetOpen) return;
        if (sheetMode !== "paying") return;
        if (!pollMetaRef.current.active) return;
        const id = pollMetaRef.current.taskId;
        if (!id) return;
        const st = String(intent?.status || "").toLowerCase();
        if (isTerminalStatus(st)) return;
        const dt = Date.now() - (pollMetaRef.current.lastTickAt || 0);
        if (dt > 9000) {
          log("poll watchdog restart", id, st, `dt=${dt}`);
          startPolling(id);
        }
      } catch (_) {}
    }, 3000);
    return () => clearInterval(iv);
  }, [sheetOpen, sheetMode, intent?.status]);

  // Best-effort cancel when user closes app/tab while not taken.
  useEffect(() => {
    const handler = () => {
      try {
        const id = getIntentId(intent);
        if (!id) return;
        const st = String(intent?.status || "").toLowerCase();
        if (!isCancellableStatus(st)) return;

        const token = getToken();
        const url = endpoints.cancelIntent(id);
        log("pagehide cancel", id, st);
        // keepalive works better than sendBeacon because we need Authorization
        fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ reason: "pagehide" }),
          keepalive: true,
        }).catch(() => {});
      } catch (_) {}
    };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, [intent?.task_id, intent?.status]);

  const startTelegramScanPopup = ({ allowNonIOS = false } = {}) => {
    // Default behavior: iOS only.
    // On Android we can explicitly allow this as a fallback when web camera fails.
    if (!allowNonIOS && !isIOS()) return false;
    if (!canUseTelegramNativeScanner()) return false;
    const tg = window.Telegram.WebApp;

    if (tgScanRef.current.active) {
      setNativeScanActive(true);
      return true;
    }
    tgScanRef.current.active = true;
    tgScanRef.current.closeByApp = false;
    tgScanRef.current.handledQr = false;
    setNativeScanActive(true);

    const detachNativeScannerEvents = () => {
      try {
        if (tgScanRef.current.onQr) tg.offEvent?.("qrTextReceived", tgScanRef.current.onQr);
      } catch (_) {}
      try {
        if (tgScanRef.current.onClose) tg.offEvent?.("scanQrPopupClosed", tgScanRef.current.onClose);
      } catch (_) {}
      tgScanRef.current.onQr = null;
      tgScanRef.current.onClose = null;
    };

    const onQr = (payload) => {
      if (tgScanRef.current.handledQr) return;
      tgScanRef.current.handledQr = true;
      try {
        const txt = normalizeQrText(payload?.data || payload);
        if (txt) {
          tgScanRef.current.closeByApp = true;
          void onQrDetected(txt);
        }
      } finally {
        try {
          tg.closeScanQrPopup();
        } catch (_) {}
      }
    };

    const onClose = () => {
      const closedByApp = !!tgScanRef.current.closeByApp;
      detachNativeScannerEvents();
      endTrustedOverlay("tg-qr-popup");
      tgScanRef.current.active = false;
      tgScanRef.current.closeByApp = false;
      tgScanRef.current.handledQr = false;
      setNativeScanActive(false);
      // User cancelled native scanner -> go back to home.
      // If popup was closed by app right after qrTextReceived, stay in payment flow.
      if (closedByApp) return;
      navigate("/");
    };

    tgScanRef.current.onQr = onQr;
    tgScanRef.current.onClose = onClose;

    try {
      tg.onEvent("qrTextReceived", onQr);
      tg.onEvent("scanQrPopupClosed", onClose);
      // Нативный сканер прячет webview — не считать это уходом из приложения.
      beginTrustedOverlay("tg-qr-popup");
      tg.showScanQrPopup({ text: t("qr.hint") });
      return true;
    } catch (_) {
      // If anything fails, fall back to web camera.
      detachNativeScannerEvents();
      endTrustedOverlay("tg-qr-popup");
      tgScanRef.current.active = false;
      tgScanRef.current.closeByApp = false;
      tgScanRef.current.handledQr = false;
      setNativeScanActive(false);
      return false;
    }
  };

  const shouldIgnore = (text) => {
    const now = Date.now();
    if (processingRef.current) return true;
    if (sheetOpen) return true;
    if (text && lastRef.current.text === text && now - lastRef.current.ts < 1500) return true;
    lastRef.current = { text, ts: now };
    return false;
  };

  const stopCamera = async () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;

    const s = streamRef.current;
    streamRef.current = null;
    try {
      if (s) s.getTracks().forEach((t) => t.stop());
    } catch (_) {}

    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
        v.srcObject = null;
      } catch (_) {}
    }

    setIsTorchSupported(false);
    setIsTorchOn(false);
    setIsTorchBusy(false);
  };

  const isStreamActive = () => {
    try {
      const stream = streamRef.current;
      if (!stream) return false;
      const tracks = stream.getVideoTracks ? stream.getVideoTracks() : [];
      return tracks.some((track) => track && track.readyState === "live" && track.enabled !== false);
    } catch (_) {
      return false;
    }
  };

  const getMainVideoTrack = () => {
    const stream = streamRef.current;
    if (!stream?.getVideoTracks) return null;
    const tracks = stream.getVideoTracks();
    return tracks?.[0] || null;
  };

  const isTorchTrackSupported = (track) => {
    try {
      const caps = track?.getCapabilities?.() || {};
      if (typeof caps.torch === "boolean") return caps.torch;
      if (Array.isArray(caps.fillLightMode)) return caps.fillLightMode.includes("torch");
      return false;
    } catch (_) {
      return false;
    }
  };

  const refreshTorchStateFromTrack = () => {
    const track = getMainVideoTrack();
    if (!track) {
      setIsTorchSupported(false);
      setIsTorchOn(false);
      setIsTorchBusy(false);
      return { track: null, supported: false };
    }
    const supported = isTorchTrackSupported(track);
    setIsTorchSupported(supported);
    if (!supported) {
      setIsTorchOn(false);
      return { track, supported };
    }

    try {
      const currentTorch = Boolean(track.getSettings?.().torch);
      setIsTorchOn(currentTorch);
    } catch (_) {
      setIsTorchOn(false);
    }
    return { track, supported };
  };

  const clearScannerRecoveryTimer = () => {
    if (scannerRecoveryTimerRef.current) {
      clearTimeout(scannerRecoveryTimerRef.current);
      scannerRecoveryTimerRef.current = null;
    }
  };

  const clearGalleryErrorTimer = () => {
    if (galleryErrorTimerRef.current) {
      clearTimeout(galleryErrorTimerRef.current);
      galleryErrorTimerRef.current = null;
    }
  };

  const showGalleryScanError = (message) => {
    clearGalleryErrorTimer();
    setPhase("error");
    setErrorText(message);
    galleryErrorTimerRef.current = setTimeout(() => {
      galleryErrorTimerRef.current = null;
      if (!sheetOpenRef.current) {
        setPhase("scanning");
        setErrorText("");
      }
    }, 2200);
  };

  const loadImageFromFile = async (file) => {
    if (typeof createImageBitmap === "function") {
      try {
        return await createImageBitmap(file, { imageOrientation: "from-image" });
      } catch (_) {}
      try {
        return await createImageBitmap(file);
      } catch (_) {}
    }

    const objectUrl = URL.createObjectURL(file);
    try {
      return await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("image_load_failed"));
        img.src = objectUrl;
      });
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  const readQrFromImageFile = async (file) => {
    const img = await loadImageFromFile(file);
    const sourceWidth = Number(img?.width || img?.naturalWidth || 0);
    const sourceHeight = Number(img?.height || img?.naturalHeight || 0);
    if (!sourceWidth || !sourceHeight) return "";

    const scanCanvas = document.createElement("canvas");
    const scanCtx = scanCanvas.getContext("2d", { willReadFrequently: true });
    if (!scanCtx) return "";

    const longestSide = Math.max(sourceWidth, sourceHeight);
    const scaleCandidates =
      longestSide > 2200
        ? [2200 / longestSide, 1800 / longestSide, 1400 / longestSide]
        : [1, 0.8, 0.65];

    try {
      for (const scaleCandidate of scaleCandidates) {
        const scale = Math.min(1, Math.max(0.35, scaleCandidate));
        const width = Math.max(1, Math.round(sourceWidth * scale));
        const height = Math.max(1, Math.round(sourceHeight * scale));

        scanCanvas.width = width;
        scanCanvas.height = height;
        scanCtx.drawImage(img, 0, 0, width, height);

        const imageData = scanCtx.getImageData(0, 0, width, height);
        const code = jsQR(imageData.data, width, height, { inversionAttempts: "attemptBoth" });
        if (code?.data) return normalizeQrText(code.data);
      }
      return "";
    } finally {
      if (typeof img?.close === "function") {
        try {
          img.close();
        } catch (_) {}
      }
    }
  };

  const initDetector = () => {
    try {
      if (typeof window !== "undefined" && "BarcodeDetector" in window) {
        // eslint-disable-next-line no-undef
        detectorRef.current = new BarcodeDetector({ formats: ["qr_code"] });
        useNativeRef.current = true;
        return;
      }
    } catch (_) {}
    detectorRef.current = null;
    useNativeRef.current = false;
  };

  const startCamera = async (restart = false) => {
    if (sheetOpen) return;
    if (cameraStartingRef.current) return;
    cameraStartingRef.current = true;
    clearGalleryErrorTimer();

    setPhase("scanning");
    setErrorText("");
    setVideoReady(false);
    setLockScale(1);
    setBoxN(null);
    smoothRef.current.has = false;
    setFreezeBox(null);
    freezeRef.current.until = 0;
    stableRef.current = { lastText: "", firstSeenMs: 0, lastSeenMs: 0 };

    try {
      if (restart) await stopCamera();
      initDetector();

      if (!streamRef.current) {
        const hasMediaDevices =
          typeof navigator !== "undefined" &&
          navigator.mediaDevices &&
          typeof navigator.mediaDevices.getUserMedia === "function";
        if (!hasMediaDevices) throw new Error("media_devices_unavailable");

        const openWithConstraints = async (constraints) => {
          return navigator.mediaDevices.getUserMedia({
            video: constraints,
            audio: false,
          });
        };

        let lastErr = null;
        let stream = null;
        const permissionState = await getCameraPermissionState();

        if (permissionState === "prompt") {
          // Important for Telegram Android: keep a single permission request,
          // but still ask rear camera first to avoid opening selfie camera.
          const constraintsList = [
            { facingMode: { ideal: "environment" } },
            { facingMode: "environment" },
            true,
          ];
          for (const c of constraintsList) {
            try {
              stream = await openWithConstraints(c);
              if (stream) break;
            } catch (e) {
              lastErr = e;
              log("camera open failed (single prompt mode)", c, e?.name || "unknown", e?.message || "");
              if (isCameraPermissionError(e)) break;
            }
          }
        } else {
          const constraintsList = [
            // Preferred: rear camera (most devices)
            { facingMode: { ideal: "environment" } },
            // Some Android WebViews behave better with plain facingMode string.
            { facingMode: "environment" },
            // Fallback without camera side hint.
            { width: { ideal: 1280 }, height: { ideal: 720 } },
            true,
          ];

          for (const c of constraintsList) {
            try {
              stream = await openWithConstraints(c);
              if (stream) break;
            } catch (e) {
              lastErr = e;
              log("camera open failed", c, e?.name || "unknown", e?.message || "");
              if (isCameraPermissionError(e)) break;
            }
          }
        }

        // Some WebViews still return front camera even with facingMode hints.
        // If that happens and another camera exists, switch to a likely back camera by deviceId.
        if (stream) {
          try {
            const [track] = stream.getVideoTracks ? stream.getVideoTracks() : [];
            const facingMode = String(track?.getSettings?.().facingMode || "").toLowerCase();
            const currentDeviceId = String(track?.getSettings?.().deviceId || "");

            if (!facingMode || facingMode === "user") {
              const devices = await navigator.mediaDevices.enumerateDevices();
              const videoDevices = (devices || []).filter((d) => d.kind === "videoinput");
              const backByLabel = videoDevices.find(
                (d) =>
                  d?.deviceId &&
                  d.deviceId !== currentDeviceId &&
                  /(back|rear|environment|зад|traseira|trasera)/i.test(String(d.label || ""))
              );
              const fallbackOther = videoDevices.find(
                (d) => d?.deviceId && d.deviceId !== currentDeviceId
              );
              const candidate = backByLabel || fallbackOther;
              if (candidate?.deviceId) {
                let swapped = null;
                try {
                  swapped = await openWithConstraints({
                    deviceId: { exact: candidate.deviceId },
                    facingMode: { ideal: "environment" },
                  });
                } catch (_) {
                  swapped = await openWithConstraints({ deviceId: { exact: candidate.deviceId } });
                }
                if (swapped) {
                  try {
                    stream.getTracks().forEach((t) => t.stop());
                  } catch (_) {}
                  stream = swapped;
                }
              }
            }
          } catch (e) {
            log("camera rear switch skipped", e?.name || "unknown", e?.message || "");
          }
        }

        // Extra fallback: explicitly pick a back camera by deviceId if available.
        if (!stream) {
          try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = (devices || []).filter((d) => d.kind === "videoinput");
            const back = videoDevices.find((d) =>
              /(back|rear|environment|зад|traseira|trasera)/i.test(String(d.label || ""))
            );
            const fallbackDevice = back || videoDevices[0];
            if (fallbackDevice?.deviceId) {
              stream = await openWithConstraints({ deviceId: { exact: fallbackDevice.deviceId } });
            }
          } catch (e) {
            lastErr = e;
            log("camera deviceId fallback failed", e?.name || "unknown", e?.message || "");
          }
        }

        // Android retry: camera can fail on first attempt in Telegram WebView.
        if (!stream && isAndroid() && permissionState !== "prompt") {
          try {
            await sleep(260);
            stream = await openWithConstraints({ facingMode: { ideal: "environment" } });
            if (!stream) stream = await openWithConstraints(true);
          } catch (e) {
            lastErr = e;
            log("camera android retry failed", e?.name || "unknown", e?.message || "");
          }
        }

        if (!stream) throw lastErr || new Error("camera_open_failed");
        streamRef.current = stream;
      }

      refreshTorchStateFromTrack();

      const video = videoRef.current;
      if (!video) return;

      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.muted = true;

      if (video.srcObject !== streamRef.current) video.srcObject = streamRef.current;

      try {
        await video.play();
      } catch (_) {}

      waitFirstFrame(video, 2500).then((ok) => setVideoReady(!!ok));
      scanLoop();
    } catch (e) {
      if (USE_TELEGRAM_NATIVE_SCANNER) {
        const fallbackToNative = startTelegramScanPopup({ allowNonIOS: true });
        if (fallbackToNative) {
          setPhase("scanning");
          setErrorText("");
          return;
        }
      }

      log("camera fatal", e?.name || "unknown", e?.message || "");
      setPhase("error");
      setErrorText(t("qr.error_camera"));
      tgHapticNotify("error");
    } finally {
      cameraStartingRef.current = false;
    }
  };

  const updateOverlayNormalized = (viewEl, boxViewPx) => {
    const rect = viewEl.getBoundingClientRect();
    const viewW = rect.width;
    const viewH = rect.height;
    setBoxN({
      x: clamp(boxViewPx.x / viewW, 0, 1),
      y: clamp(boxViewPx.y / viewH, 0, 1),
      w: clamp(boxViewPx.w / viewW, 0, 1),
      h: clamp(boxViewPx.h / viewH, 0, 1),
    });
  };

  const smoothTowards = (viewEl, targetBoxViewPx, nowMs) => {
    if (freezeRef.current.until && nowMs < freezeRef.current.until && freezeBox) return;

    const alpha = 0.28;
    const s = smoothRef.current;
    if (!s.has) {
      s.has = true;
      s.x = targetBoxViewPx.x;
      s.y = targetBoxViewPx.y;
      s.w = targetBoxViewPx.w;
      s.h = targetBoxViewPx.h;
    } else {
      s.x = lerp(s.x, targetBoxViewPx.x, alpha);
      s.y = lerp(s.y, targetBoxViewPx.y, alpha);
      s.w = lerp(s.w, targetBoxViewPx.w, alpha);
      s.h = lerp(s.h, targetBoxViewPx.h, alpha);
    }
    s.lastSeenMs = nowMs;
    updateOverlayNormalized(viewEl, { x: s.x, y: s.y, w: s.w, h: s.h });
  };

  const decayIfLost = (viewEl, nowMs, fallbackBoxViewPx) => {
    if (freezeRef.current.until && nowMs < freezeRef.current.until && freezeBox) return;

    const s = smoothRef.current;
    if (!s.has) {
      updateOverlayNormalized(viewEl, fallbackBoxViewPx);
      return;
    }
    const HOLD_MS = 420;
    if (nowMs - s.lastSeenMs <= HOLD_MS) {
      updateOverlayNormalized(viewEl, { x: s.x, y: s.y, w: s.w, h: s.h });
      return;
    }
    const t = 0.08;
    s.x = lerp(s.x, fallbackBoxViewPx.x, t);
    s.y = lerp(s.y, fallbackBoxViewPx.y, t);
    s.w = lerp(s.w, fallbackBoxViewPx.w, t);
    s.h = lerp(s.h, fallbackBoxViewPx.h, t);
    updateOverlayNormalized(viewEl, { x: s.x, y: s.y, w: s.w, h: s.h });
  };

  const detectWithNative = async (video, viewEl) => {
    const detector = detectorRef.current;
    if (!detector) return null;

    const canvas = canvasRef.current;
    if (!canvas) return null;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    // Slightly higher sampling helps with small QR codes (minimal change).
    const targetW = Math.min(1280, vw);
    const scale = targetW / vw;
    const targetH = Math.round(vh * scale);

    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, targetW, targetH);
    const barcodes = await detector.detect(canvas);
    if (!barcodes || barcodes.length === 0) return null;

    let best = barcodes[0];
    let bestArea = 0;
    for (const b of barcodes) {
      const bb = b.boundingBox;
      if (bb) {
        const area = bb.width * bb.height;
        if (area > bestArea) {
          bestArea = area;
          best = b;
        }
      }
    }

    const raw = best?.rawValue || "";
    if (!raw) return null;

    const unscale = 1 / scale;

    let ptsCanvas = null;
    if (best.cornerPoints && best.cornerPoints.length >= 4) {
      ptsCanvas = best.cornerPoints.map((p) => ({ x: p.x, y: p.y }));
    } else if (best.boundingBox) {
      const bb = best.boundingBox;
      ptsCanvas = [
        { x: bb.x, y: bb.y },
        { x: bb.x + bb.width, y: bb.y },
        { x: bb.x + bb.width, y: bb.y + bb.height },
        { x: bb.x, y: bb.y + bb.height },
      ];
    }

    if (!ptsCanvas) return { text: normalizeQrText(raw), boxViewPx: null };

    const ptsVideo = ptsCanvas.map((p) => ({ x: p.x * unscale, y: p.y * unscale }));
    const rect = viewEl.getBoundingClientRect();
    const viewW = rect.width;
    const viewH = rect.height;

    const ptsView = ptsVideo.map((p) => mapVideoToView(p.x, p.y, vw, vh, viewW, viewH));
    const pad = Math.max(10, Math.min(viewW, viewH) * 0.014);
    const boxView = boxFromPoints(ptsView, pad);

    return { text: normalizeQrText(raw), boxViewPx: boxView };
  };

  const detectWithJsQR = (video, viewEl) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    // Slightly higher sampling helps with small QR codes (minimal change).
    const targetW = Math.min(1024, vw);
    const scale = targetW / vw;
    const targetH = Math.round(vh * scale);

    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, targetW, targetH);
    const imageData = ctx.getImageData(0, 0, targetW, targetH);

    const code = jsQR(imageData.data, targetW, targetH, { inversionAttempts: "attemptBoth" });
    if (!code?.data || !code.location) return null;

    const unscale = 1 / scale;
    const loc = code.location;

    const ptsVideo = [
      { x: loc.topLeftCorner.x * unscale, y: loc.topLeftCorner.y * unscale },
      { x: loc.topRightCorner.x * unscale, y: loc.topRightCorner.y * unscale },
      { x: loc.bottomRightCorner.x * unscale, y: loc.bottomRightCorner.y * unscale },
      { x: loc.bottomLeftCorner.x * unscale, y: loc.bottomLeftCorner.y * unscale },
    ];

    const rect = viewEl.getBoundingClientRect();
    const viewW = rect.width;
    const viewH = rect.height;

    const ptsView = ptsVideo.map((p) => mapVideoToView(p.x, p.y, vw, vh, viewW, viewH));
    const pad = Math.max(10, Math.min(viewW, viewH) * 0.014);
    const boxView = boxFromPoints(ptsView, pad);

    return { text: normalizeQrText(code.data), boxViewPx: boxView };
  };

  const isMissingAmountDetail = (detail) => {
    const code =
      detail && typeof detail === "object" ? String(detail.code || "").toLowerCase() : "";
    if (code === "manual_amount_required") return true;
    const d = typeof detail === "string" ? String(detail || "").toLowerCase() : "";
    return (
      d === "resolve_amount_not_found" ||
      d === "resolve_amount_failed" ||
      d === "autobanking_not_ready" ||
      d.includes("without amount") ||
      d.includes("without sum")
    );
  };

  const handleCreateIntentError = async (err, sourceQrLink = "") => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const detailCode =
      detail && typeof detail === "object" ? String(detail.code || "").toLowerCase() : "";
    const message = `${String(err?.code || "")} ${String(err?.message || "")}`.trim();
    const prettyError = formatCreateIntentError({ status, detail, message, t });
    const isTimeoutOrNetwork = looksLikeTimeoutOrNetwork(message);

    if (isTimeoutOrNetwork) {
      try {
        const token = getToken();
        const activeRes = await axios.get(endpoints.getActiveIntent, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 6000,
        });
        const active = activeRes?.data;
        if (active?.task_id) {
          openSheet(active);
          setPhase("scanning");
          return;
        }
      } catch (_) {
        // ignore and show readable error below
      }
    }

    // Fallback to manual amount input only after backend response.
    // This avoids false positives from frontend-only QR heuristics.
    const manualRequiredByBackend = detailCode === "manual_amount_required";
    if (
      sourceQrLink &&
      (manualRequiredByBackend || (isMissingAmountDetail(detail) && isLikelyMissingAmountQr(sourceQrLink)))
    ) {
      openManualAmountSheet(sourceQrLink);
      setPhase("scanning");
      return;
    }

    setSheetMode("error");
    setSheetError(prettyError || t("qr.error_processing"));
    tgHapticNotify("error");
    setPhase("scanning");
  };

  const createIntentFromQrLink = async (qrLink, conflictSourceLink = qrLink, options = {}) => {
    const token = getToken();
    if (!token) throw new Error(t("common.error_no_token"));
    if (!user?.id) throw new Error(t("common.error_no_userid"));

    const createPayload = {
      user_id: user.id,
      qr_link: qrLink,
      pay_currency: selectedPayCurrency,
      dynamic_qr: Boolean(options?.dynamicQr),
    };
    const manualAmountRub = Number(options?.manualAmountRub);
    if (Boolean(options?.dynamicQr) && Number.isFinite(manualAmountRub) && manualAmountRub > 0) {
      createPayload.manual_amount_rub = Math.round(manualAmountRub * 100) / 100;
    }
    const createConfig = {
      headers: { Authorization: `Bearer ${token}` },
      timeout: getCreateIntentTimeoutMs(qrLink),
    };
    const postCreateIntent = async () => {
      let retriedCooldown = false;
      let busyRetries = 0;
      while (true) {
        try {
          return await axios.post(endpoints.createIntent, createPayload, createConfig);
        } catch (e) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          if (isAmountResolvingBusyError(e) && busyRetries < 4) {
            busyRetries += 1;
            await new Promise((resolve) => setTimeout(resolve, 1200));
            continue;
          }
          if (status === 429 && detail?.code === "cooldown" && !retriedCooldown) {
            const waitSec = Math.max(1, Number(detail?.retry_after || 1));
            await new Promise((resolve) => setTimeout(resolve, waitSec * 1000));
            retriedCooldown = true;
            continue;
          }
          throw e;
        }
      }
    };
    const res = await postCreateIntent();
    const data = res?.data;
    if (!data?.task_id) throw new Error(t("common.error_create_payment"));

    // If backend returns an already-existing active intent for a DIFFERENT QR,
    // show a clear "payment already in progress" screen.
    try {
      const scanned = parseMultiQrInfo(conflictSourceLink);
      const active = parseMultiQrInfo(data?.qr_link);
      const isDifferent =
        (scanned.uuid && active.uuid && scanned.uuid !== active.uuid) ||
        (data?.qr_link && String(data.qr_link) !== String(conflictSourceLink));
      if (isDifferent) {
        setScanAttemptLink(qrLink);
        setIntent(data);
        setSheetError("");
        setSheetMode("conflict");
        setPhase("scanning");
        return;
      }
    } catch (_) {
      // ignore parse errors; fallback to normal flow
    }

    openSheet(data);
    setPhase("scanning");
  };

  const handleManualAmountConfirm = async () => {
    if (!manualQrLink) {
      setSheetMode("error");
      setSheetError(t("qr.error_processing"));
      return;
    }
    const parsedFromInput = parseRubAmountInput(manualAmountInput);
    let amount = parsedFromInput || Number(manualAmountRub || 0);
    if (!(amount > 0)) {
      setManualAmountError(t("common.valid_amount_error"));
      return;
    }
    if (parsedFromInput) setManualAmountValue(parsedFromInput);

    processingRef.current = true;
    setSheetError("");
    setManualAmountError("");
    setCancelReady(false);
    setIsIntentCreating(false);
    setIntent(null);
    setPhase("locking");
    setSheetMode("locking");
    setSheetOpen(true);

    try {
      await createIntentFromQrLink(manualQrLink, manualQrLink, {
        dynamicQr: true,
        manualAmountRub: amount,
      });
    } catch (err) {
      await handleCreateIntentError(err, manualQrLink);
    } finally {
      processingRef.current = false;
    }
  };

  const onQrDetected = async (decodedText) => {
    if (!decodedText) return;
    if (shouldIgnore(decodedText)) return;

    const normalizedText = normalizeQrText(decodedText);
    const parsedQrInfo = parseMultiQrInfo(normalizedText);
    const hasInlineAmount = Number(parsedQrInfo?.amountRub) > 0;

    processingRef.current = true;
    setSheetError("");
    setCancelReady(false);
    setIntent(null);
    setIsIntentCreating(hasInlineAmount);

    try {
      await stopCamera();
    } catch (_) {}

    if (hasInlineAmount) {
      openPreviewSheetFromQr(normalizedText, parsedQrInfo.amountRub);
      setPhase("scanning");
    } else {
      setPhase("locking");
      setSheetMode("locking");
      setSheetOpen(true);
    }

    try {
      await createIntentFromQrLink(normalizedText, normalizedText);
    } catch (err) {
      await handleCreateIntentError(err, normalizedText);
    } finally {
      setIsIntentCreating(false);
      processingRef.current = false;
    }
  };

  const handleToggleTorch = async () => {
    if (sheetOpen || isTorchBusy) return;
    const { track, supported } = refreshTorchStateFromTrack();
    if (!track || !supported) return;

    const nextTorch = !isTorchOn;
    setIsTorchBusy(true);
    try {
      await track.applyConstraints({ advanced: [{ torch: nextTorch }] });
      setIsTorchOn(nextTorch);
      tgHapticImpact("light");
    } catch (e) {
      log("torch toggle failed", e?.name || "unknown", e?.message || "");
      showGalleryScanError(
        t("qr.error_flashlight_toggle", {
          defaultValue: "Не удалось переключить фонарик.",
        })
      );
      setIsTorchOn(false);
      tgHapticNotify("error");
    } finally {
      setIsTorchBusy(false);
    }
  };

  const handlePickFromGalleryClick = () => {
    if (sheetOpen || processingRef.current) return;
    clearGalleryErrorTimer();
    if (USE_TELEGRAM_NATIVE_SCANNER) {
      try {
        const tg = window?.Telegram?.WebApp;
        if (tgScanRef.current.active && tg) {
          tgScanRef.current.closeByApp = true;
          tg.closeScanQrPopup?.();
        }
      } catch (_) {}
    }
    galleryInputRef.current?.click?.();
  };

  const exitGalleryModeWithError = (message) => {
    const text = String(message || t("qr.error_processing"));
    toast.error(text);
    tgHapticNotify("error");
    navigate("/");
  };

  const processGalleryFile = async (file) => {
    if (!file || sheetOpenRef.current) return;
    try {
      const qrText = await readQrFromImageFile(file);
      if (!qrText) {
        const msg = t("qr.error_qr_not_found_in_image", {
          defaultValue: "QR-код на фото не найден. Попробуйте другое изображение.",
        });
        if (autoPickGallery) {
          exitGalleryModeWithError(msg);
          return;
        }
        showGalleryScanError(msg);
        tgHapticNotify("error");
        return;
      }
      await onQrDetected(qrText);
    } catch (_) {
      const msg = t("qr.error_gallery_open", {
        defaultValue: "Не удалось прочитать изображение из галереи.",
      });
      if (autoPickGallery) {
        exitGalleryModeWithError(msg);
        return;
      }
      showGalleryScanError(msg);
      tgHapticNotify("error");
    }
  };

  const handleGalleryImageSelected = async (event) => {
    const input = event?.target;
    const file = input?.files?.[0];
    if (input) input.value = "";
    if (!file) return;
    await processGalleryFile(file);
  };

  const scanLoop = async () => {
    rafRef.current = requestAnimationFrame(scanLoop);

    const video = videoRef.current;
    const viewEl = video?.parentElement;
    if (!video || !viewEl) return;
    if (sheetOpen) return;
    if (!video.videoWidth || !video.videoHeight) return;

    const now = performance.now();
    if (scanLoop._last && now - scanLoop._last < 90) return;
    scanLoop._last = now;

    if (phase === "error") return;

    const rect = viewEl.getBoundingClientRect();
    const viewW = rect.width;
    const viewH = rect.height;

    const fallbackBoxViewPx = { x: viewW * 0.18, y: viewH * 0.3, w: viewW * 0.64, h: viewH * 0.4 };

    try {
      let result = null;

      if (useNativeRef.current && detectorRef.current) {
        try {
          result = await detectWithNative(video, viewEl);
        } catch (_) {
          useNativeRef.current = false;
          detectorRef.current = null;
          result = null;
        }
      }
      if (!result) result = detectWithJsQR(video, viewEl);

      if (result?.boxViewPx) {
        smoothTowards(viewEl, result.boxViewPx, now);

        if (result.text && !processingRef.current && phase === "scanning") {
          const txt = result.text;
          const st = stableRef.current;
          const sameAsPrevious = txt === st.lastText;
          const continuous = now - st.lastSeenMs < 420;

          if (!sameAsPrevious || !continuous) {
            st.lastText = txt;
            st.firstSeenMs = now;
            st.lastSeenMs = now;
          } else {
            st.lastSeenMs = now;
          }

          if (now - st.firstSeenMs >= QR_STABLE_SCAN_MS) {
            st.lastText = "";
            st.firstSeenMs = 0;
            st.lastSeenMs = 0;
            onQrDetected(txt);
          }
        }
      } else {
        decayIfLost(viewEl, now, fallbackBoxViewPx);
      }
    } catch (_) {
      decayIfLost(viewEl, now, fallbackBoxViewPx);
    }
  };

  const handleClose = () => {
    clearGalleryErrorTimer();
    clearScannerRecoveryTimer();
    stopPolling();
    stopCamera();
    setNativeInitPending(false);
    setNativeScanActive(false);
    if (USE_TELEGRAM_NATIVE_SCANNER) {
      // Close Telegram native scanner if it is open.
      try {
        const tg = window?.Telegram?.WebApp;
        if (tgScanRef.current.active && tg) tg.closeScanQrPopup?.();
        if (tgScanRef.current.onQr) tg.offEvent?.("qrTextReceived", tgScanRef.current.onQr);
        if (tgScanRef.current.onClose) tg.offEvent?.("scanQrPopupClosed", tgScanRef.current.onClose);
      } catch (_) {}
      endTrustedOverlay("tg-qr-popup");
      tgScanRef.current.active = false;
    }
    navigate("/");
  };

  useEffect(() => {
    let disposed = false;

    const bootstrapScanner = async () => {
      // Never open the camera (or the Telegram scan popup) while QR payments
      // are off: a scanned code could not be turned into an intent anyway.
      if (qrMaintenance.active) {
        setNativeInitPending(false);
        setNativeScanActive(false);
        return;
      }

      if (autoPickGallery && !autoGalleryPickTriggeredRef.current) {
        autoGalleryPickTriggeredRef.current = true;
        setNativeInitPending(false);
        setNativeScanActive(false);
        const pendingFile = consumePendingGalleryFileFromHome();
        if (pendingFile) {
          setTimeout(() => {
            if (!disposed) {
              void processGalleryFile(pendingFile);
            }
          }, 20);
          return;
        }
        navigate("/");
        return;
      }

      if (!USE_TELEGRAM_NATIVE_SCANNER) {
        setNativeInitPending(false);
        startCamera(true);
        return;
      }

      setNativeInitPending(true);
      let opened = false;
      // Telegram WebApp API can become ready with a small delay.
      for (let i = 0; i < 8; i += 1) {
        if (disposed) return;
        opened = startTelegramScanPopup({ allowNonIOS: true });
        if (opened) break;
        await sleep(120);
      }
      if (disposed) return;

      setNativeInitPending(false);
      if (!opened) startCamera(true);
    };

    bootstrapScanner();

    const onVis = () => {
      if (document.hidden) {
        stopPolling();
        stopCamera();
      } else {
        const shouldRecover =
          !sheetOpenRef.current &&
          (!USE_TELEGRAM_NATIVE_SCANNER || !tgScanRef.current.active) &&
          (phaseRef.current === "error" || phaseRef.current === "scanning");
        if (shouldRecover && !isStreamActive()) {
          if (USE_TELEGRAM_NATIVE_SCANNER) {
            setNativeInitPending(true);
            const resumedNative = startTelegramScanPopup({ allowNonIOS: true });
            setNativeInitPending(false);
            if (!resumedNative) startCamera(true);
          } else {
            startCamera(true);
          }
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVis);
      clearGalleryErrorTimer();
      clearScannerRecoveryTimer();
      stopPolling();
      stopCamera();
      if (USE_TELEGRAM_NATIVE_SCANNER) {
        // Cleanup Telegram scan handlers
        try {
          const tg = window?.Telegram?.WebApp;
          if (tgScanRef.current.onQr) tg?.offEvent?.("qrTextReceived", tgScanRef.current.onQr);
          if (tgScanRef.current.onClose) tg?.offEvent?.("scanQrPopupClosed", tgScanRef.current.onClose);
          if (tgScanRef.current.active) tg?.closeScanQrPopup?.();
        } catch (_) {}
        endTrustedOverlay("tg-qr-popup");
        tgScanRef.current.active = false;
      }
      window.removeEventListener("keydown", handleKeyDown);
    };
    // qrMaintenance.active is a dependency because /api/config can resolve
    // after this page mounts: when it comes back "on", the cleanup below stops
    // the camera the first pass had already started.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPickGallery, qrMaintenance.active]);

  // ===== Data shown in sheet =====
  const amountRub = intent?.amount_rub;
  const payCurrency = normalizePayCurrency(intent?.pay_currency || selectedPayCurrency);
  const amountPayFromIntent = intent?.amount_pay ?? intent?.amount_usdt ?? intent?.amount_usd_est ?? null;
  const rateRubPerPayRaw = Number(intent?.rate_rub_per_pay_currency ?? intent?.rate_usdt_rub ?? intent?.rate_usd_rub);
  const rateRubPerPay = Number.isFinite(rateRubPerPayRaw) && rateRubPerPayRaw > 0 ? rateRubPerPayRaw : null;
  const payBalanceRow = balances?.find((b) => String(b?.currency || "").toUpperCase() === payCurrency) || null;
  const payUsdRate = Number(payBalanceRow?.usd_rate ?? market?.prices?.[payCurrency] ?? 0);
  const configUsdRubRate = Number(config?.usd_rub_rate ?? 0);
  const previewDisplayRateRubPerPay =
    configUsdRubRate > 0 && payUsdRate > 0
      ? configUsdRubRate * payUsdRate
      : null;
  // The sheet shows the rate we actually debit at, commission included, so that
  // amount_rub / rate matches the amount to pay. The backend's market rate
  // (display_rate_rub_per_pay_currency) is used only to tell the commission.
  const marketRateRubPerPayRaw = Number(intent?.display_rate_rub_per_pay_currency);
  const marketRateRubPerPay =
    Number.isFinite(marketRateRubPerPayRaw) && marketRateRubPerPayRaw > 0 ? marketRateRubPerPayRaw : null;
  const effectiveRateRubPerPay =
    rateRubPerPay != null
      ? rateRubPerPay
      : (previewDisplayRateRubPerPay != null
          ? previewDisplayRateRubPerPay * (1 - QR_DISPLAY_FALLBACK_COMMISSION_PCT)
          : null);
  const qrCommissionPct =
    rateRubPerPay != null && marketRateRubPerPay != null
      ? Math.max(0, 1 - rateRubPerPay / marketRateRubPerPay)
      : QR_DISPLAY_FALLBACK_COMMISSION_PCT;
  const qrCommissionPctLabel = String(Math.round(qrCommissionPct * 10000) / 100);
  const amountPay =
    amountPayFromIntent != null
      ? amountPayFromIntent
      : (Number(amountRub) > 0 && Number(effectiveRateRubPerPay) > 0
          ? Number(amountRub) / Number(effectiveRateRubPerPay)
          : null);
  const isManualAmountMode = sheetMode === "manual_amount";
  const manualAmountPayPreview =
    isManualAmountMode && manualAmountRub > 0 && Number(effectiveRateRubPerPay || 0) > 0
      ? Number(manualAmountRub) / Number(effectiveRateRubPerPay)
      : null;
  const balancePay =
    intent?.balance_pay_currency ??
    intent?.balance_usdt ??
    payBalanceRow?.available ??
    null;
  const availablePay =
    intent?.available_pay_currency ??
    intent?.balance_pay_currency ??
    intent?.available_usdt ??
    intent?.balance_usdt ??
    null;
  const hasFunds =
    availablePay != null &&
    amountPay != null &&
    Number(availablePay) + 1e-9 >= Number(amountPay);
  const hasIntentId = Boolean(getIntentId(intent));
  const isPreparingIntent = isIntentCreating && !hasIntentId;
  const currencyOptions = QR_PAY_CURRENCIES.map((code) => {
    const row = balances?.find((b) => String(b?.currency || "").toUpperCase() === code);
    const meta = QR_PAY_META_BY_CODE[code] || {};
    const balance = Number(row?.available ?? row?.amount ?? 0);
    return {
      code,
      name: meta.name || code,
      icon: meta.icon || "",
      balance: Number.isFinite(balance) ? balance : 0,
      hasBalance: Number.isFinite(balance) && balance > 1e-9,
      order: QR_PAY_ORDER.get(code) ?? 999,
    };
  }).sort((a, b) => {
    if (a.hasBalance !== b.hasBalance) return a.hasBalance ? -1 : 1;
    return a.order - b.order;
  });
  const selectedCurrencyOption =
    currencyOptions.find((opt) => opt.code === payCurrency) ||
    currencyOptions[0] || { code: payCurrency, name: payCurrency, balance: 0 };

  const attemptQrInfo = parseMultiQrInfo(scanAttemptLink);

  const b = freezeBox || boxN || { x: 0.18, y: 0.3, w: 0.64, h: 0.4 };
  const showCameraLayer =
    !sheetOpen &&
    !autoPickGallery &&
    (!USE_TELEGRAM_NATIVE_SCANNER || (!nativeScanActive && !nativeInitPending));
  const isAmountResolving = sheetMode === "locking";

  // Maintenance notice replaces the scanner entirely. The one exception is a
  // sheet that is already open: that means the resume effect found an intent
  // this user still has money held against, and they must keep the Cancel
  // button that releases it.
  if (qrMaintenance.active && !sheetOpen) {
    return (
      <div
        className="fixed inset-0 flex flex-col items-center justify-center bg-black px-8 text-center"
        data-testid="qr-maintenance-screen"
      >
        <button
          onClick={() => navigate("/")}
          className="absolute left-4 top-[calc(1rem+var(--lp-top))] z-30 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur-md"
          aria-label="Close"
        >
          <X className="h-6 w-6 text-white/90" />
        </button>

        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-400/15">
          <AlertCircle className="h-8 w-8 text-amber-300" />
        </div>
        <h2 className="mt-5 text-lg font-semibold text-white">{qrMaintenance.title}</h2>
        <p className="mt-2 max-w-xs text-sm leading-relaxed text-white/60">{qrMaintenance.text}</p>

        <button
          onClick={() => navigate("/")}
          className="mt-8 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-white"
          data-testid="qr-maintenance-close-btn"
        >
          {t("common.close")}
        </button>
      </div>
    );
  }

  // Под листом оплаты видна подложка страницы. Зашитый светлый #f2f2f2 светил
  // белой рамкой вокруг тёмного листа, поэтому фон берём из темы. Пока лист
  // закрыт, под ним камера — там чёрный и должен остаться чёрным.
  return (
    <div
      className="fixed inset-0"
      style={{ backgroundColor: sheetOpen ? "hsl(var(--background))" : "black" }}
    >
      <style>{`
        .bracket { position:absolute; width:42px; height:42px; border:6px solid #fff; }
        .tl { left:-3px; top:-3px; border-right:none; border-bottom:none; border-top-left-radius:20px; }
        .tr { right:-3px; top:-3px; border-left:none; border-bottom:none; border-top-right-radius:20px; }
        .bl { left:-3px; bottom:-3px; border-right:none; border-top:none; border-bottom-left-radius:20px; }
        .br { right:-3px; bottom:-3px; border-left:none; border-top:none; border-bottom-right-radius:20px; }

        @keyframes pulseBracket {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        .hud-brackets {
          position: absolute;
          inset: 0;
          animation: pulseBracket 3s ease-in-out infinite;
        }

        @keyframes sheetIn { from { transform: translateY(18px); opacity:0; } to { transform: translateY(0); opacity:1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .btnSpinner { width:18px; height:18px; border-radius:999px; border:2px solid rgba(255,255,255,0.55); border-top-color: rgba(255,255,255,0.0); animation: spin 0.7s linear infinite; }

        /* CryptoBot-ish.
           Цвета — токенами темы, а не константами. Эти правила писались под
           светлый лист: чёрный текст rgba(0,0,0,0.92) и подложка чёрным по
           4.5%. Лист давно берёт материал из темы, и в тёмной — а она у нас
           основная — сумма, курс и баланс оказывались чёрным по тёмному:
           экран оплаты читался как пустой. */
        .cardRow { background: var(--lp-tile); border-radius: 22px; padding: 14px 14px; }
        .label { font-size: 13px; color: hsl(var(--muted-foreground)); line-height: 1.15; }
        .value { font-size: 20px; font-weight: 800; color: hsl(var(--foreground)); margin-top: 8px; line-height: 1.12; }
        .sub { font-size: 15px; color: hsl(var(--muted-foreground)); margin-top: 8px; line-height: 1.2; }

        /* balance row like screenshot */
        .balanceRow { padding: 12px 6px 6px; }
        .balanceLeft { display:flex; align-items:center; gap:12px; }
        .balanceTitle { font-size: 18px; font-weight: 800; color: hsl(var(--foreground)); line-height: 1.1; }
        .balanceSub { font-size: 15px; color: hsl(var(--muted-foreground)); margin-top: 4px; }
        .balanceRightTop { font-size: 18px; font-weight: 800; color: hsl(var(--foreground)); text-align:right; line-height: 1.1; }
        .balanceRightSub { font-size: 15px; color: hsl(var(--muted-foreground)); text-align:right; margin-top: 4px; }

        .divider { height:1px; background: var(--lp-hair); margin: 14px 0; }

        .hint-pill {
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          font-weight: 500;
          font-size: 14px;
          padding: 10px 24px;
          border-radius: 999px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.25);
        }

        .sheet-center-loader-wrap {
          min-height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sheet-center-loader {
          width: 52px;
          height: 52px;
          border-radius: 999px;
          border: 4px solid rgba(47, 111, 228, 0.18);
          border-top-color: #2f6fe4;
          animation: spin 0.75s linear infinite;
        }

      `}</style>

      {/* CAMERA + OVERLAY */}
      {showCameraLayer && (
        <div className="absolute inset-0">
          {/* Exit */}
          <button
            onClick={async () => {
              try {
                await stopCamera();
              } catch (_) {}
              navigate("/");
            }}
            className="absolute left-4 top-[calc(1rem+var(--lp-top))] z-30 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-white/90" />
          </button>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            style={{ opacity: videoReady ? 1 : 0, transition: "opacity 220ms ease" }}
          />
          {!videoReady && <div className="absolute inset-0 bg-black" />}
          <canvas ref={canvasRef} className="hidden" />

          {/* Overlay */}
          <div className="absolute inset-0 pointer-events-none z-10">
            <div className="absolute inset-0 bg-black/12" />
            <div
              style={{
                position: "absolute",
                left: `${b.x * 100}%`,
                top: `${b.y * 100}%`,
                width: `${b.w * 100}%`,
                height: `${b.h * 100}%`,
                transform: `scale(${lockScale})`,
                transformOrigin: "center",
                transition:
                  "left 160ms cubic-bezier(.2,.9,.2,1), top 160ms cubic-bezier(.2,.9,.2,1), width 160ms cubic-bezier(.2,.9,.2,1), height 160ms cubic-bezier(.2,.9,.2,1), transform 180ms cubic-bezier(.2,.9,.2,1)",
              }}
            >
              <div className="hud-brackets">
                <div className="bracket tl" />
                <div className="bracket tr" />
                <div className="bracket bl" />
                <div className="bracket br" />
              </div>
            </div>
          </div>

          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleGalleryImageSelected}
          />
          <div className="absolute bottom-6 right-6 z-30 flex items-center gap-3">
            <button
              type="button"
              onClick={handlePickFromGalleryClick}
              className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-md border border-white/15 flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.35)] active:scale-95 transition-transform"
              aria-label={t("qr.open_gallery", { defaultValue: "Открыть галерею" })}
            >
              <ImageIcon className="w-6 h-6 text-white/90" />
            </button>

            <button
              type="button"
              onClick={handleToggleTorch}
              disabled={!isTorchSupported || isTorchBusy}
              className={`w-12 h-12 rounded-full backdrop-blur-md border flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,0.35)] transition-transform ${
                isTorchOn
                  ? "bg-yellow-400/30 border-yellow-200/50 text-yellow-100"
                  : "bg-black/40 border-white/15 text-white/90"
              } ${
                !isTorchSupported || isTorchBusy
                  ? "opacity-45 cursor-not-allowed"
                  : "active:scale-95"
              }`}
              aria-label={t("qr.toggle_flashlight", { defaultValue: "Фонарик" })}
            >
              {isTorchOn ? (
                <Flashlight className="w-6 h-6" />
              ) : (
                <FlashlightOff className="w-6 h-6" />
              )}
            </button>
          </div>

          {phase === "scanning" && (
            <div className="absolute top-4 left-16 right-6 z-20 pointer-events-none">
              <div className="flex justify-center">
                <div className="hint-pill max-w-[90%] text-center">{t("qr.hint")}</div>
              </div>
            </div>
          )}

          {phase === "error" && (
            <div className="absolute bottom-0 left-0 right-0 z-20 p-6">
              <div className="mx-auto max-w-[420px] text-center text-white bg-destructive/20 backdrop-blur-xl rounded-2xl p-4 border border-destructive/25">
                <div className="flex items-center justify-center gap-2 font-semibold mb-1">
                  <AlertCircle className="w-5 h-5" /> {t("common.error")}
                </div>
                <div className="text-sm text-white/90 break-words">{errorText}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom sheet */}
      {sheetOpen && (
        <div className="absolute inset-0 z-40">
          <div
            className="absolute inset-0"
            style={{
              background: "rgba(0,0,0,0.25)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
            onClick={closeSheet}
          />

          {/* Выезд листа задан материалом (.lp-sheet в styles/glass.css):
              своя анимация здесь накладывалась на общую и давала двойной ход. */}
          <div className="absolute left-0 right-0 bottom-0">
            <div
              className="mx-auto w-full max-w-[560px] lp-sheet lp-solid !rounded-t-[26px] overflow-hidden"
              style={{
                boxShadow: "0 -18px 60px rgba(0,0,0,0.25)",
                maxHeight: "calc(100vh - max(var(--lp-top), 60px))",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* SCROLL CONTENT */}
              <div style={{ overflow: "auto" }}>
                <div className="flex items-center justify-center pt-3">
                  <div className="w-10 h-1.5 rounded-full bg-[var(--lp-tile)]" />
                </div>

                <div className="flex items-center justify-between px-6 pt-5">
                  <div className="text-[22px] font-semibold text-foreground">
                    {sheetMode === "conflict" ? "Оплата уже в процессе" : t("qr.payment")}
                  </div>
                  <button onClick={closeSheet} className="w-10 h-10 rounded-full bg-[var(--lp-tile)] flex items-center justify-center">
                    <X className="w-6 h-6 text-muted-foreground" />
                  </button>
                </div>

                <div className="px-6 pt-3 pb-5 text-center">
                  {sheetMode === "conflict" ? (
                    <div className="text-muted-foreground text-[16px] leading-snug">
                      У вас уже есть незавершенная оплата.
                      <div className="mt-3 text-foreground">
                        <div>
                          Текущий QR: <span className="font-semibold">{fmtRub(amountRub)}</span>
                        </div>
                        {attemptQrInfo?.amountRub != null && (
                          <div className="mt-1">
                            Новый QR: <span className="font-semibold">{fmtRub(attemptQrInfo.amountRub)}</span>
                          </div>
                        )}
                      </div>
                      {/* uuid intentionally hidden from user */}
                    </div>
                  ) : sheetMode === "manual_amount" ? (
                    <div className="text-muted-foreground text-[18px] font-normal">
                      {t("qr.payment_by_qr")}
                    </div>
                  ) : isAmountResolving ? (
                    <div className="sheet-center-loader-wrap" aria-label={t("qr.resolving_amount")}>
                      <div className="sheet-center-loader" />
                    </div>
                  ) : (
                    <>
                      <div className="text-muted-foreground text-[18px]">
                        {t("qr.payment_by_qr")} <span className="font-semibold text-muted-foreground">QR-code</span>
                      </div>
                      <div
                        className={`mt-2 leading-[1.03] tracking-tight text-foreground ${
                          isAmountResolving
                            ? "text-[36px] font-bold text-blue-600"
                            : "text-[48px] font-extrabold"
                        }`}
                      >
                        {isAmountResolving ? t("qr.resolving_amount") : fmtRub(amountRub)}
                      </div>
                    </>
                  )}
                </div>

                <div style={{ height: 6 }} />
                <div className="px-5 pb-2">
                  {!isAmountResolving && (
                    <>
                      {isManualAmountMode && (
                        <div className="cardRow mb-4">
                          <div className="min-w-0 w-full">
                            <div className="label">{t("qr.enter_amount_short", { defaultValue: "Введите сумму" })}</div>
                            <div className="mt-2 relative">
                              <input
                                ref={manualAmountInputRef}
                                type="text"
                                inputMode="decimal"
                                value={manualAmountInput}
                                onChange={(e) => {
                                  const next = String(e.target.value || "").replace(/[^\d.,]/g, "");
                                  setManualAmountInput(next);
                                  setManualAmountError("");
                                  if (!next.trim()) {
                                    setManualAmountRub(0);
                                    return;
                                  }
                                  const parsed = parseRubAmountInput(next);
                                  if (parsed) setManualAmountRub(parsed);
                                }}
                                onBlur={() => {
                                  if (!String(manualAmountInput || "").trim()) return;
                                  const parsed = parseRubAmountInput(manualAmountInput);
                                  if (!parsed) {
                                    setManualAmountError(t("common.valid_amount_error"));
                                    return;
                                  }
                                  setManualAmountValue(parsed);
                                }}
                                placeholder="0"
                                className="h-11 w-full rounded-xl lp-field pl-3 pr-10 text-[22px] font-semibold text-foreground outline-none"
                              />
                              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[22px] font-semibold text-foreground leading-none">
                                ₽
                              </span>
                            </div>
                            {manualAmountError && (
                              <div className="mt-3 text-sm text-destructive-fg bg-destructive/10 rounded-xl px-3 py-2">
                                {manualAmountError}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="cardRow mb-4 relative" ref={currencyMenuRef}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="label">{t("qr.select_currency", { defaultValue: "Currency" })}</div>
                          </div>
                          <button
                            type="button"
                            disabled={
                              !(sheetMode === "preview" || sheetMode === "manual_amount") ||
                              isQuoteLoading ||
                              isPreparingIntent
                            }
                            onClick={() => setIsCurrencyMenuOpen((prev) => !prev)}
                            className="h-11 min-w-[170px] rounded-xl lp-field px-3 text-[15px] font-semibold text-foreground disabled:opacity-60"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <CurrencyIcon code={selectedCurrencyOption.code} size={20} className="w-5 h-5 rounded-full shrink-0" />
                                <span className="truncate">{selectedCurrencyOption.code}</span>
                                <span className="text-[12px] font-medium text-muted-foreground">
                                  ({fmtCurrencyAmount(selectedCurrencyOption.balance, selectedCurrencyOption.code, { baseDigits: 4, minDigits: 0 })})
                                </span>
                              </div>
                              <ChevronDown
                                className={`w-4 h-4 text-muted-foreground transition-transform ${
                                  isCurrencyMenuOpen ? "rotate-180" : ""
                                }`}
                              />
                            </div>
                          </button>
                        </div>

                        {isCurrencyMenuOpen &&
                          !isPreparingIntent &&
                          (sheetMode === "preview" || sheetMode === "manual_amount") && (
                          <div className="lp-veil lp-veil--edged absolute left-0 right-0 top-[calc(100%+10px)] z-50 rounded-[16px] overflow-hidden">
                            <div className="max-h-64 overflow-y-auto">
                              {currencyOptions.map((opt) => (
                                <button
                                  key={opt.code}
                                  type="button"
                                  onClick={() => handlePayCurrencyChange(opt.code)}
                                  className="w-full px-3 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-[var(--lp-tile)] active:bg-[var(--lp-tile)]"
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <CurrencyIcon code={opt.code} size={22} className="w-[22px] h-[22px] rounded-full shrink-0" />
                                    <div className="min-w-0">
                                      <div className="text-[15px] font-semibold text-foreground leading-tight">{opt.code}</div>
                                      <div className="text-[12px] text-muted-foreground leading-tight">{opt.name}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[13px] font-medium text-muted-foreground">
                                      {fmtCurrencyAmount(opt.balance, opt.code, { baseDigits: 4, minDigits: 0 })}
                                    </span>
                                    {opt.code === payCurrency && <Check className="w-4 h-4 text-blue-600" />}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Amount card */}
                      <div className="cardRow mb-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <div className="label flex items-start gap-0.5">
                              <span>{t("qr.amount_to_pay")}</span>
                              {(amountPay != null || manualAmountPayPreview != null) && qrCommissionPct > 0 && (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button
                                      type="button"
                                      className="inline-block bg-transparent p-0 text-[14px] font-semibold leading-none text-muted-foreground translate-y-[-2px]"
                                      aria-label={t("qr.amount_note", {
                                        pct: qrCommissionPctLabel,
                                        defaultValue: `Курс включает комиссию конвертации ${qrCommissionPctLabel}%.`,
                                      })}
                                    >
                                      *
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    side="top"
                                    align="start"
                                    className="w-auto max-w-[220px] rounded-2xl lp-field px-3 py-2 text-[13px] leading-snug text-muted-foreground shadow-[0_12px_32px_rgba(0,0,0,0.16)]"
                                  >
                                    {t("qr.amount_note", {
                                      pct: qrCommissionPctLabel,
                                      defaultValue: `Курс включает комиссию конвертации ${qrCommissionPctLabel}%.`,
                                    })}
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                            {sheetMode === "manual_amount" ? (
                              <div className="value">
                                {manualAmountPayPreview == null
                                  ? "—"
                                  : `${fmtCurrencyAmount(manualAmountPayPreview, payCurrency, { baseDigits: 6 })} ${payCurrency}`}
                              </div>
                            ) : (
                              <div className="value">
                                {amountPay == null
                                  ? "—"
                                  : `${fmtCurrencyAmount(amountPay, payCurrency, { baseDigits: 6 })} ${payCurrency}`}
                              </div>
                            )}
                          </div>
                          <div
                            className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                            style={{
                              background: "rgba(38,161,123,0.16)",
                              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
                            }}
                          >
                            <CurrencyIcon code={payCurrency} size={28} className="w-7 h-7 rounded-full" />
                          </div>
                        </div>
                      </div>

                      {/* Rate card */}
                      <div className="cardRow">
                        <div className="min-w-0">
                          <div className="label">
                            {t("qr.price_per_coin", {
                              currency: payCurrency,
                              defaultValue: t("qr.price_per_usdt"),
                            })}
                          </div>
                          <div className="value">
                            {effectiveRateRubPerPay == null ? "—" : `${fmtNum(effectiveRateRubPerPay, 2)} ₽`}
                          </div>
                          <div className="sub">{t("common.exchange_rate")}</div>
                        </div>
                      </div>
                    </>
                  )}

                  {!isAmountResolving && (
                    <>
                      <div className="divider" />

                      {/* Balance row */}
                      <div className="balanceRow">
                        <div className="flex items-center justify-between gap-4">
                          <div className="balanceLeft">
                            <CurrencyIcon code={payCurrency} size={40} className="w-10 h-10 rounded-full" />
                            <div>
                              <div className="balanceTitle">{payCurrency}</div>
                              <div className="balanceSub">
                                {t("qr.payment_in_currency", {
                                  currency: payCurrency,
                                  defaultValue: t("qr.payment_in_usdt"),
                                })}
                              </div>
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div className="balanceRightTop">
                              {balancePay == null
                                ? "—"
                                : `${fmtCurrencyAmount(balancePay, payCurrency, { baseDigits: 6 })} ${payCurrency}`}
                            </div>
                            <div className="balanceRightSub">{t("qr.your_balance")}</div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {sheetMode === "error" && (
                    <div className="mt-4 bg-destructive/10 text-destructive-fg rounded-2xl p-3 text-sm">
                      {sheetError || t("common.error")}
                    </div>
                  )}

                  {sheetMode === "success" && (
                    <div className="mt-5 flex flex-col items-center justify-center gap-2">
                      <div className="text-[18px] font-semibold text-foreground">{t("qr.paid")}</div>
                      <div className="text-xs text-muted-foreground">{t("qr.auto_close")}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* STICKY FOOTER */}
              <div
                className="px-5"
                style={{
                  paddingTop: 14,
                  paddingBottom: "max(var(--lp-bottom), 22px)",
                  // Подвал с кнопкой оплаты — часть листа, а не отдельная
                  // белая полоса: зашитый white светился поперёк тёмного
                  // листа, а надписи на нём пропадали. Стык обозначаем
                  // волосяной линией темы — чёрная тень в тёмной невидима.
                  background: "hsl(var(--background))",
                  boxShadow: "0 -1px 0 var(--lp-hair)",
                }}
              >
                <div style={{ paddingBottom: 6 }}>
                  {sheetMode === "conflict" && (
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={handleConflictContinue}
                        className="w-full h-[58px] rounded-2xl bg-primary text-primary-foreground text-[19px] font-semibold active:opacity-90"
                      >
                        Продолжить текущую оплату
                      </button>

                      {Boolean(intent?.can_cancel) ? (
                        <button
                          onClick={handleConflictCancelAndStartNew}
                          className="w-full h-[58px] rounded-2xl bg-[var(--lp-tile)] text-foreground text-[19px] font-semibold active:opacity-90"
                        >
                          Отменить текущую и оплатить новый QR
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full h-[58px] rounded-2xl bg-[var(--lp-tile)] text-muted-foreground text-[19px] font-semibold"
                        >
                          Нельзя отменить — уже в работе
                        </button>
                      )}
                    </div>
                  )}

                  {sheetMode === "locking" && (
                    <button
                      disabled
                      className="w-full h-[58px] rounded-2xl bg-primary text-primary-foreground text-[19px] font-semibold opacity-95 flex items-center justify-center gap-3"
                    >
                      <span className="btnSpinner" />
                      {t("qr.resolving_amount")}
                    </button>
                  )}

                  {sheetMode === "manual_amount" &&
                    (manualAmountRub > 0 ? (
                      <button
                        onClick={handleManualAmountConfirm}
                        className="w-full h-[58px] rounded-2xl bg-primary text-primary-foreground text-[19px] font-semibold active:opacity-90"
                      >
                        {t("qr.continue_with_amount", { defaultValue: "Продолжить" })}
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full h-[58px] rounded-2xl bg-[var(--lp-tile)] text-muted-foreground text-[19px] font-semibold"
                      >
                        {t("qr.enter_amount_required", { defaultValue: "Введите сумму" })}
                      </button>
                    ))}

                  {sheetMode === "preview" &&
                    (isPreparingIntent || isQuoteLoading ? (
                      <button
                        disabled
                        className="w-full h-[58px] rounded-2xl bg-primary text-primary-foreground text-[19px] font-semibold opacity-95 flex items-center justify-center gap-3"
                      >
                        <span className="btnSpinner" />
                        {isPreparingIntent
                          ? t("qr.preparing_payment", { defaultValue: "Подготавливаем оплату..." })
                          : t("common.loading")}
                      </button>
                    ) : hasFunds && hasIntentId ? (
                      <button
                        onClick={handlePay}
                        className="w-full h-[58px] rounded-2xl bg-primary text-primary-foreground text-[19px] font-semibold active:opacity-90"
                      >
                        {t("common.pay")}
                      </button>
                    ) : !hasIntentId ? (
                      <button
                        disabled
                        className="w-full h-[58px] rounded-2xl bg-[var(--lp-tile)] text-muted-foreground text-[19px] font-semibold"
                      >
                        {t("qr.preparing_payment", { defaultValue: "Подготавливаем оплату..." })}
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full h-[58px] rounded-2xl bg-[var(--lp-tile)] text-muted-foreground text-[19px] font-semibold"
                      >
                        {t("common.insufficient_balance")}
                      </button>
                    ))}

                  {sheetMode === "paying" && (
                    <>
                      {!cancelReady ? (
                        <button
                          disabled
                          className="w-full h-[58px] rounded-2xl bg-primary text-primary-foreground text-[19px] font-semibold opacity-95 flex items-center justify-center gap-3"
                        >
                          <span className="btnSpinner" />
                          {t("common.pay")}
                        </button>
                      ) : (
                        <button
                          onClick={handleCancel}
                          disabled={!Boolean(intent?.can_cancel)}
                          className={`w-full h-[58px] rounded-2xl text-[19px] font-semibold active:opacity-90 ${
                            intent?.can_cancel ? "bg-[var(--lp-tile)] text-foreground" : "bg-[var(--lp-tile)] text-muted-foreground"
                          }`}
                        >
                          {t("common.cancel")}
                        </button>
                      )}
                    </>
                  )}

                  {sheetMode === "success" && (
                    <button
                      onClick={closeSheet}
                      className="w-full h-[58px] rounded-2xl bg-primary text-primary-foreground text-[19px] font-semibold active:opacity-90"
                    >
                      {t("common.ok")}
                    </button>
                  )}

                  {sheetMode === "error" && (
                    <button
                      onClick={closeSheet}
                      className="w-full h-[58px] rounded-2xl bg-[var(--lp-tile)] text-foreground text-[19px] font-semibold active:opacity-90"
                    >
                      {t("common.close")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
